import type { SubmitScoreResult } from './LeaderboardApi'

/**
 * Hash algorithm used for score sealing
 */
export const HASH_ALGORITHM_WEB_CRYPTO = 'SHA-256'
export const HASH_ALGORITHM_NODE = 'sha256'

/**
 * Compute HMAC-SHA256 hash for score sealing.
 * Hash always includes: score, duration, token
 * Matches server-side hash computation exactly.
 *
 * @param score - The score value
 * @param duration - Duration in seconds
 * @param token - Score token
 * @param sealingNonce - One-time nonce for this submission
 * @param sealingSecret - Secret key for HMAC
 * @returns Hex-encoded hash string
 */
export async function computeScoreHash(
  score: number,
  duration: number,
  token: string,
  sealingNonce: string,
  sealingSecret: string,
): Promise<string> {
  // Build payload: score:value|duration:value|token:value
  const payload = `score:${score}|duration:${duration}|token:${token}`
  const fullPayload = `${payload}|nonce:${sealingNonce}`

  // HMAC-SHA256 using Web Crypto API
  const encoder = new TextEncoder()
  const keyData = encoder.encode(sealingSecret)
  const messageData = encoder.encode(fullPayload)

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: HASH_ALGORITHM_WEB_CRYPTO },
    false,
    ['sign'],
  )

  const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData)

  // Convert to hex string
  return Array.from(new Uint8Array(signature))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}


/**
 * Map a submit-score transport error to a typed, non-throwing
 * {@link SubmitScoreResult} the game can branch on — or `undefined` when the
 * error is a genuine failure that should still throw. Shared by both the RPC and
 * HTTP leaderboard impls so every submission surface resolves the same
 * pending → ranked/rejected outcomes (identity change, replay rejection) instead
 * of one path throwing an opaque error.
 */
export function mapSubmitScoreFailure(error: unknown): SubmitScoreResult | undefined {
  const message = error instanceof Error ? error.message : String(error)
  // Token minted under a different identity (mid-session account switch).
  if (/token does not belong to profile/i.test(message)) {
    return { accepted: false, failureReason: 'identity_changed', reason: message }
  }
  // requiresReplay board rejected the score (mismatch / invalid replay / verifier
  // unavailable) — the server's safe message is "Score could not be verified".
  if (/could not be verified|replay verification failed/i.test(message)) {
    return { accepted: false, failureReason: 'replay_rejected', reason: message }
  }
  return undefined
}
